import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { CATEGORIE, STATI, OPERATORI } from "@/lib/format";
import { CLI } from "@/constants/testIds";
import api from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
  ragione_sociale: "",
  sede: "",
  citta: "",
  provincia: "",
  cap: "",
  email: "",
  telefono: "",
  fax: "",
  resp_acquisti_nome: "",
  resp_acquisti_telefono: "",
  resp_acquisti_email: "",
  referente_nome: "",
  referente_telefono: "",
  referente_email: "",
  categoria: "altro",
  stato: "potenziale",
  operatore_commerciale: "",
  note_generali: "",
};

export default function ClientDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
  }, [open, initial]);

  const isEdit = !!initial?.id;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.ragione_sociale.trim()) {
      toast.error("La ragione sociale è obbligatoria");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      let res;
      if (isEdit) {
        res = await api.put(`/clients/${initial.id}`, payload);
        toast.success("Cliente aggiornato");
      } else {
        res = await api.post("/clients", payload);
        toast.success("Cliente creato");
      }
      onOpenChange(false);
      window.dispatchEvent(new Event("crm:data-changed"));
      onSaved && onSaved(res.data);
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={CLI.dialog} className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold tracking-tight">
            {isEdit ? "Modifica Cliente" : "Nuovo Cliente"}
          </DialogTitle>
          <DialogDescription className="text-stone-500">
            Compila la scheda anagrafica. I campi con * sono obbligatori.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Ragione Sociale *</Label>
            <Input
              data-testid={CLI.fieldRagioneSociale}
              value={form.ragione_sociale}
              onChange={(e) => set("ragione_sociale", e.target.value)}
              className="mt-1.5"
            />
          </div>

          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Sede</Label>
            <Input data-testid={CLI.fieldSede} value={form.sede} onChange={(e) => set("sede", e.target.value)} className="mt-1.5" />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Città</Label>
            <Input data-testid={CLI.fieldCitta} value={form.citta} onChange={(e) => set("citta", e.target.value)} className="mt-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Provincia</Label>
              <Input data-testid={CLI.fieldProvincia} value={form.provincia} onChange={(e) => set("provincia", e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">CAP</Label>
              <Input data-testid={CLI.fieldCap} value={form.cap} onChange={(e) => set("cap", e.target.value)} className="mt-1.5" />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Email</Label>
            <Input data-testid={CLI.fieldEmail} value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Telefono</Label>
            <Input data-testid={CLI.fieldTelefono} value={form.telefono} onChange={(e) => set("telefono", e.target.value)} className="mt-1.5" />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Fax</Label>
            <Input data-testid={CLI.fieldFax} value={form.fax} onChange={(e) => set("fax", e.target.value)} className="mt-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Categoria</Label>
              <Select value={form.categoria} onValueChange={(v) => set("categoria", v)}>
                <SelectTrigger data-testid={CLI.fieldCategoria} className="mt-1.5 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {CATEGORIE.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Stato</Label>
              <Select value={form.stato} onValueChange={(v) => set("stato", v)}>
                <SelectTrigger data-testid={CLI.fieldStato} className="mt-1.5 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {STATI.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Operatore Commerciale</Label>
            <Select
              value={form.operatore_commerciale || "__none__"}
              onValueChange={(v) => set("operatore_commerciale", v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="client-field-operatore" className="mt-1.5 bg-white">
                <SelectValue placeholder="Seleziona operatore" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value="__none__">— Nessuno —</SelectItem>
                {OPERATORI.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[11px] text-stone-400 mt-1">
              Chi ha inserito o segue il cliente
            </div>
          </div>

          <div className="md:col-span-2 mt-2 pt-4 border-t border-stone-100">
            <div className="text-xs uppercase tracking-widest text-[#F56A1F] font-semibold mb-3">Responsabile Acquisti</div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Nome</Label>
            <Input data-testid={CLI.fieldRespNome} value={form.resp_acquisti_nome} onChange={(e) => set("resp_acquisti_nome", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Telefono</Label>
            <Input data-testid={CLI.fieldRespTel} value={form.resp_acquisti_telefono} onChange={(e) => set("resp_acquisti_telefono", e.target.value)} className="mt-1.5" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Email</Label>
            <Input data-testid={CLI.fieldRespEmail} value={form.resp_acquisti_email} onChange={(e) => set("resp_acquisti_email", e.target.value)} className="mt-1.5" />
          </div>

          <div className="md:col-span-2 mt-2 pt-4 border-t border-stone-100">
            <div className="text-xs uppercase tracking-widest text-[#F56A1F] font-semibold mb-3">Referente Secondario</div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Nome</Label>
            <Input data-testid={CLI.fieldRefNome} value={form.referente_nome} onChange={(e) => set("referente_nome", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Telefono</Label>
            <Input data-testid={CLI.fieldRefTel} value={form.referente_telefono} onChange={(e) => set("referente_telefono", e.target.value)} className="mt-1.5" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Email</Label>
            <Input data-testid={CLI.fieldRefEmail} value={form.referente_email} onChange={(e) => set("referente_email", e.target.value)} className="mt-1.5" />
          </div>

          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Note Generali</Label>
            <Textarea
              data-testid={CLI.fieldNoteGen}
              value={form.note_generali}
              onChange={(e) => set("note_generali", e.target.value)}
              rows={3}
              className="mt-1.5"
            />
          </div>
        </div>

        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid={CLI.saveBtn} onClick={submit} disabled={saving}
            className="bg-[#F56A1F] hover:bg-[#E05206] text-white">
            {saving ? "Salvataggio…" : (isEdit ? "Salva Modifiche" : "Crea Cliente")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
